const SESSION_KEY = 'chamii:dev:session:v1';
const OIDC_REQ_KEY = 'chamii:dev:oidc:req:v1';
const IAM_NEW_USER_HINT_KEY = 'chamii:dev:iam:new-user:v1';
const OIDC_DEBUG_KEY = 'chamii:dev:oidc:debug:v1';
const OIDC_OVERRIDE_KEY = 'chamii:dev:oidc:override:v1';
const OIDC_RETRY_KEY = 'chamii:dev:oidc:retry:v1';
const FLASH_TOAST_KEY = 'chamii:dev:toast:flash:v1';
const LOGOUT_MARK_KEY = 'chamii:dev:logout:mark:v1';
const SESSION_EXPIRED_KEY = 'chamii:auth:session-expired:v1';
const OIDC_REAUTH_KEY = 'chamii:auth:oidc-reauth:v1';
const OIDC_REAUTH_MAX_AGE_MS = 2 * 60 * 1000;

export function saveSession(session) {
  const tokens = session?.tokens || {};
  const safeSession = {
    ...session,
    tokens: {
      accessToken: typeof tokens.accessToken === 'string' ? tokens.accessToken : '',
      ...(typeof tokens.idToken === 'string' ? { idToken: tokens.idToken } : {}),
      ...(typeof tokens.expiresIn === 'number' ? { expiresIn: tokens.expiresIn } : {}),
    },
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(safeSession));
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
}

export function loadSession() {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw);
    const tokens = session?.tokens || {};
    const accessToken = typeof tokens?.accessToken === 'string' ? tokens.accessToken.trim() : '';
    const claims = decodeJwtPayload(accessToken);
    const exp = claims?.exp;
    if (!accessToken || !Number.isInteger(exp) || exp <= Math.floor(Date.now() / 1000)) {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
      return null;
    }
    if (tokens.refreshToken !== undefined) saveSession(session);
    return tokens.refreshToken === undefined ? session : JSON.parse(sessionStorage.getItem(SESSION_KEY));
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
    return null;
  }
}

export function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

export function hasSessionExpired() {
  return sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
}

function readOidcReauthorization() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(OIDC_REAUTH_KEY) || 'null');
    if (!parsed || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > OIDC_REAUTH_MAX_AGE_MS) {
      sessionStorage.removeItem(OIDC_REAUTH_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(OIDC_REAUTH_KEY);
    return null;
  }
}

export function requestOidcReauthorization() {
  if (readOidcReauthorization()) return false;
  sessionStorage.setItem(OIDC_REAUTH_KEY, JSON.stringify({ phase: 'requested', at: Date.now() }));
  return true;
}

export function claimOidcReauthorization() {
  const current = readOidcReauthorization();
  if (current?.phase === 'started') return false;
  sessionStorage.setItem(OIDC_REAUTH_KEY, JSON.stringify({ phase: 'started', at: Date.now() }));
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  return true;
}

export function clearOidcReauthorization() {
  sessionStorage.removeItem(OIDC_REAUTH_KEY);
}

export function saveOidcRequest(req) {
  sessionStorage.setItem(OIDC_REQ_KEY, JSON.stringify(req));
}

export function loadOidcRequest() {
  const raw = sessionStorage.getItem(OIDC_REQ_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearOidcRequest() {
  sessionStorage.removeItem(OIDC_REQ_KEY);
}

export function saveOidcDebug(debug) {
  try {
    sessionStorage.setItem(OIDC_DEBUG_KEY, JSON.stringify(debug || {}));
  } catch {
    // ignore
  }
}

export function loadOidcDebug() {
  const raw = sessionStorage.getItem(OIDC_DEBUG_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearOidcDebug() {
  sessionStorage.removeItem(OIDC_DEBUG_KEY);
}

export function saveOidcOverride(override) {
  const payload = {
    v: 1,
    at: Date.now(),
    baseUrl: typeof override?.baseUrl === 'string' ? override.baseUrl : undefined,
    clientId: typeof override?.clientId === 'string' ? override.clientId : undefined,
  };
  try {
    sessionStorage.setItem(OIDC_OVERRIDE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function loadOidcOverride(maxAgeMs = 10 * 60 * 1000) {
  try {
    const raw = sessionStorage.getItem(OIDC_OVERRIDE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1) return null;
    const at = Number(parsed.at || 0) || 0;
    if (at && Date.now() - at > maxAgeMs) {
      sessionStorage.removeItem(OIDC_OVERRIDE_KEY);
      return null;
    }
    const baseUrl = typeof parsed.baseUrl === 'string' ? parsed.baseUrl.trim() : '';
    const clientId = typeof parsed.clientId === 'string' ? parsed.clientId.trim() : '';
    if (!baseUrl && !clientId) return null;
    return { baseUrl: baseUrl || undefined, clientId: clientId || undefined, at: at || undefined };
  } catch {
    return null;
  }
}

export function clearOidcOverride() {
  try {
    sessionStorage.removeItem(OIDC_OVERRIDE_KEY);
  } catch {
    // ignore
  }
}

export function saveOidcRetry(retry) {
  const payload = {
    v: 1,
    at: Date.now(),
    count: Number.isFinite(Number(retry?.count)) ? Number(retry.count) : 0,
    reason: typeof retry?.reason === 'string' ? retry.reason : undefined,
  };
  try {
    sessionStorage.setItem(OIDC_RETRY_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function loadOidcRetry(maxAgeMs = 10 * 60 * 1000) {
  const raw = sessionStorage.getItem(OIDC_RETRY_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1) return null;
    const at = Number(parsed.at || 0) || 0;
    if (at && Date.now() - at > maxAgeMs) {
      sessionStorage.removeItem(OIDC_RETRY_KEY);
      return null;
    }
    const count = Number(parsed.count || 0) || 0;
    const reason = typeof parsed.reason === 'string' ? parsed.reason : undefined;
    return { count, reason, at: at || undefined };
  } catch {
    return null;
  }
}

export function clearOidcRetry() {
  try {
    sessionStorage.removeItem(OIDC_RETRY_KEY);
  } catch {
    // ignore
  }
}

export function decodeJwtPayload(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function saveIamNewUserHint(hint) {
  sessionStorage.setItem(IAM_NEW_USER_HINT_KEY, JSON.stringify(hint));
}

export function loadIamNewUserHint() {
  const raw = sessionStorage.getItem(IAM_NEW_USER_HINT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearIamNewUserHint() {
  sessionStorage.removeItem(IAM_NEW_USER_HINT_KEY);
}

export function setFlashToast(payload) {
  try {
    sessionStorage.setItem(FLASH_TOAST_KEY, JSON.stringify(payload || null));
  } catch {}
}

export function takeFlashToast() {
  const raw = sessionStorage.getItem(FLASH_TOAST_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(FLASH_TOAST_KEY);
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function markLogout() {
  try {
    sessionStorage.setItem(LOGOUT_MARK_KEY, JSON.stringify({ v: 1, at: Date.now() }));
  } catch {
    // ignore
  }
}

export function consumeLogoutMark(maxAgeMs = 30 * 1000) {
  let raw = null;
  try {
    raw = sessionStorage.getItem(LOGOUT_MARK_KEY);
  } catch {
    raw = null;
  }
  if (!raw) return false;
  try {
    sessionStorage.removeItem(LOGOUT_MARK_KEY);
  } catch {
    // ignore
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1) return true;
    const at = Number(parsed.at || 0) || 0;
    if (!at) return true;
    return Date.now() - at <= maxAgeMs;
  } catch {
    return true;
  }
}
